import { useEffect, useMemo, useRef, useState } from "react";
import Shell from "../components/Shell";
import {
  fetchGroups,
  fetchBroadcasts,
  createBroadcast,
  deleteBroadcast,
  generateBroadcastImage,
  draftBroadcastText,
  fetchStarterTemplates,
} from "../lib/api";
import {
  MegaphoneIcon,
  MagnifyingGlassIcon,
  ImageIcon,
  SparkleIcon,
  TrashIcon,
  UploadSimpleIcon,
  WarningIcon,
  CheckCircleIcon,
  ClockIcon,
  CaretDownIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function defaultSchedule() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setSeconds(0, 0);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

const STATUS_STYLES = {
  scheduled: "bg-zinc-100 text-zinc-700 border-zinc-300",
  sending: "bg-amber-100 text-amber-800 border-amber-300",
  sent: "bg-green-100 text-green-800 border-green-300",
  partial: "bg-amber-100 text-amber-800 border-amber-300",
  failed: "bg-red-100 text-red-700 border-red-300",
};

export default function BroadcastPage() {
  const [groups, setGroups] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [text, setText] = useState("");
  const [imageB64, setImageB64] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState("");
  const [scheduledAt, setScheduledAt] = useState(defaultSchedule());
  const [stagger, setStagger] = useState(true);
  const [topic, setTopic] = useState("");
  const [imgPrompt, setImgPrompt] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const fileRef = useRef(null);

  const loadGroups = async () => setGroups(await fetchGroups());
  const loadBroadcasts = async () => setBroadcasts(await fetchBroadcasts());

  useEffect(() => {
    loadGroups();
    loadBroadcasts();
    fetchStarterTemplates()
      .then((r) => setTemplates(r.templates || []))
      .catch(() => {});
    const t = setInterval(loadBroadcasts, 15000);
    return () => clearInterval(t);
  }, []);

  const applyTemplate = (tpl) => {
    setText(tpl.text);
    setShowTemplates(false);
    toast.success(`Loaded template: ${tpl.title}`);
  };

  const visibleGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groups.filter((g) => !q || (g.group_name || "").toLowerCase().includes(q));
  }, [groups, query]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = visibleGroups.every((g) => next.has(g.group_id));
      visibleGroups.forEach((g) => (allSelected ? next.delete(g.group_id) : next.add(g.group_id)));
      return next;
    });
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      setImagePreview(result);
      setImageB64(result.split(",")[1] || null);
    };
    reader.readAsDataURL(file);
  };

  const onGenerateFromMessage = async () => {
    if (!text.trim()) return toast.error("Write the post text first");
    setGenerating(true);
    try {
      const { image_base64 } = await generateBroadcastImage({ message: text.trim() });
      setImageB64(image_base64);
      setImagePreview(`data:image/jpeg;base64,${image_base64}`);
      toast.success("Image created from your message");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Image generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const onGenerate = async () => {
    if (!imgPrompt.trim()) return toast.error("Describe the image you want");
    setGenerating(true);
    try {
      const { image_base64 } = await generateBroadcastImage({ prompt: imgPrompt.trim() });
      setImageB64(image_base64);
      setImagePreview(`data:image/jpeg;base64,${image_base64}`);
      toast.success("Image generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Image generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const onDraft = async () => {
    if (!topic.trim()) return toast.error("Enter a topic to draft");
    setDrafting(true);
    try {
      const { text: drafted } = await draftBroadcastText(topic.trim());
      setText(drafted);
      toast.success("Draft ready — edit as you like");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draft failed");
    } finally {
      setDrafting(false);
    }
  };

  const clearImage = () => {
    setImageB64(null);
    setImagePreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const resetForm = () => {
    setText("");
    clearImage();
    setSelected(new Set());
    setTopic("");
    setImgPrompt("");
    setScheduledAt(defaultSchedule());
    setStagger(true);
  };

  const onSchedule = async () => {
    if (selected.size === 0) return toast.error("Select at least one group");
    if (!text.trim() && !imageB64) return toast.error("Add text and/or an image");
    if (!scheduledAt) return toast.error("Pick a date & time");
    const when = new Date(scheduledAt);
    if (when.getTime() < Date.now() - 60000) return toast.error("Pick a time in the future");

    const ids = [...selected];
    const names = ids.map((id) => groups.find((g) => g.group_id === id)?.group_name || id);
    setSaving(true);
    try {
      await createBroadcast({
        text: text.trim(),
        image_base64: imageB64,
        group_ids: ids,
        group_names: names,
        scheduled_at: when.toISOString(),
        stagger,
      });
      toast.success(`Broadcast scheduled to ${ids.length} group(s)`);
      resetForm();
      loadBroadcasts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not schedule");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteBroadcast(id);
      setBroadcasts((b) => b.filter((x) => x.id !== id));
      toast.success("Broadcast removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  const allVisibleSelected = visibleGroups.length > 0 && visibleGroups.every((g) => selected.has(g.group_id));

  return (
    <Shell>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="broadcast-page">
        <div className="flex items-center gap-3">
          <MegaphoneIcon size={28} weight="duotone" className="text-zinc-800" />
          <div>
            <h1 className="text-2xl font-semibold text-zinc-900">Broadcast</h1>
            <p className="text-sm text-zinc-500">Schedule one post (image + text) to many WhatsApp groups at once.</p>
          </div>
        </div>

        <div className="flex items-start gap-3 border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="ban-warning">
          <WarningIcon size={20} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
          <p>
            Sending the same post to many groups can flag your personal WhatsApp number. Keep{" "}
            <strong>random spacing</strong> on, avoid posting to dozens of groups at once, and don't broadcast too often.
          </p>
        </div>

        {/* Composer */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Left: content */}
          <div className="space-y-4 border border-zinc-200 bg-white p-4">
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">Post text</label>
                {templates.length > 0 && (
                  <div className="relative">
                    <button
                      data-testid="broadcast-templates-btn"
                      onClick={() => setShowTemplates((s) => !s)}
                      className="flex items-center gap-1 border border-zinc-300 px-2 py-1 text-xs text-zinc-700 hover:bg-zinc-50"
                    >
                      Use a template ({templates.length})
                      <CaretDownIcon size={12} className={showTemplates ? "rotate-180 transition-transform" : "transition-transform"} />
                    </button>
                    {showTemplates && (
                      <div
                        data-testid="broadcast-templates-list"
                        className="absolute right-0 z-20 mt-1 max-h-72 w-80 overflow-y-auto border border-zinc-300 bg-white shadow-lg"
                      >
                        {templates.map((t, i) => (
                          <button
                            key={i}
                            data-testid={`broadcast-template-${i}`}
                            onClick={() => applyTemplate(t)}
                            className="block w-full border-b border-zinc-100 px-3 py-2 text-left last:border-b-0 hover:bg-zinc-50"
                          >
                            <div className="text-sm font-medium text-zinc-800">{i + 1}. {t.title}</div>
                            <div className="mt-0.5 line-clamp-2 text-xs text-zinc-400">{t.text.replace(/\n/g, " ")}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <textarea
                data-testid="broadcast-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={6}
                placeholder="Write your post… or draft it with AI below."
                className="mt-1 w-full resize-none border border-zinc-300 p-2 text-sm outline-none focus:border-zinc-900"
              />
              <div className="mt-2 flex gap-2">
                <input
                  data-testid="broadcast-topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="AI draft topic (e.g. weekly FCL rates Adriatic)"
                  className="flex-1 border border-zinc-300 px-2 py-1.5 text-sm outline-none focus:border-zinc-900"
                />
                <button
                  data-testid="broadcast-draft-btn"
                  onClick={onDraft}
                  disabled={drafting}
                  className="flex items-center gap-1.5 border border-zinc-900 bg-zinc-900 px-3 py-1.5 text-sm text-white hover:bg-zinc-700 disabled:opacity-50"
                >
                  <SparkleIcon size={16} weight="fill" />
                  {drafting ? "Drafting…" : "AI draft"}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">Image (optional)</label>
              {imagePreview ? (
                <div className="mt-1 relative">
                  <img src={imagePreview} alt="preview" className="max-h-56 w-full object-contain border border-zinc-200 bg-zinc-50" data-testid="broadcast-image-preview" />
                  <button
                    data-testid="broadcast-image-clear"
                    onClick={clearImage}
                    className="absolute right-2 top-2 flex items-center gap-1 border border-zinc-300 bg-white/90 px-2 py-1 text-xs text-zinc-700 hover:bg-white"
                  >
                    <TrashIcon size={14} /> Remove
                  </button>
                </div>
              ) : (
                <div className="mt-1 flex flex-col items-center gap-2 border border-dashed border-zinc-300 p-4 text-zinc-400">
                  <ImageIcon size={28} />
                  <span className="text-xs">No image selected</span>
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="broadcast-file-input" />
                <button
                  data-testid="broadcast-generate-from-message-btn"
                  onClick={onGenerateFromMessage}
                  disabled={generating || !text.trim()}
                  className="flex items-center gap-1.5 border border-zinc-900 bg-zinc-900 px-3 py-1.5 text-sm text-white hover:bg-zinc-700 disabled:opacity-50"
                >
                  <SparkleIcon size={16} weight="fill" />
                  {generating ? "Generating…" : "Generate from message"}
                </button>
                <button
                  data-testid="broadcast-upload-btn"
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-sm text-zinc-800 hover:bg-zinc-50"
                >
                  <UploadSimpleIcon size={16} /> Upload
                </button>
              </div>
              <p className="mt-1.5 text-xs text-zinc-500">
                We'll create a clean, relevant image from your post text (no text baked in). Not right? Describe your own below, or upload one.
              </p>
              <div className="mt-2 flex gap-2">
                <input
                  data-testid="broadcast-img-prompt"
                  value={imgPrompt}
                  onChange={(e) => setImgPrompt(e.target.value)}
                  placeholder="Optional: describe a different image (e.g. cargo ship at Koper port, sunrise)"
                  className="flex-1 border border-zinc-300 px-2 py-1.5 text-sm outline-none focus:border-zinc-900"
                />
                <button
                  data-testid="broadcast-generate-btn"
                  onClick={onGenerate}
                  disabled={generating || !imgPrompt.trim()}
                  className="flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-sm text-zinc-800 hover:bg-zinc-50 disabled:opacity-50"
                >
                  <SparkleIcon size={16} />
                  Regenerate
                </button>
              </div>
            </div>
          </div>

          {/* Right: targets + schedule */}
          <div className="space-y-4 border border-zinc-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                Target groups · <span className="text-zinc-900">{selected.size} selected</span>
              </label>
              <button data-testid="broadcast-select-all" onClick={selectAllVisible} className="text-xs text-zinc-600 underline hover:text-zinc-900">
                {allVisibleSelected ? "Clear visible" : "Select visible"}
              </button>
            </div>
            <div className="flex items-center gap-2 border border-zinc-300 px-2">
              <MagnifyingGlassIcon size={16} className="text-zinc-400" />
              <input
                data-testid="broadcast-group-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search groups"
                className="w-full py-1.5 text-sm outline-none"
              />
            </div>
            <div className="max-h-64 overflow-y-auto border border-zinc-200" data-testid="broadcast-group-list">
              {visibleGroups.length === 0 && <p className="p-3 text-sm text-zinc-400">No groups found.</p>}
              {visibleGroups.map((g) => (
                <label
                  key={g.group_id}
                  data-testid={`broadcast-group-${g.group_id}`}
                  className="flex cursor-pointer items-center gap-2 border-b border-zinc-100 px-3 py-2 text-sm hover:bg-zinc-50"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(g.group_id)}
                    onChange={() => toggle(g.group_id)}
                    className="accent-zinc-900"
                  />
                  <span className="flex-1 truncate text-zinc-800">{g.group_name || g.group_id}</span>
                  {!g.enabled && <span className="text-[10px] uppercase text-zinc-400">not monitored</span>}
                </label>
              ))}
            </div>

            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">Send at</label>
              <input
                data-testid="broadcast-schedule-at"
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="mt-1 w-full border border-zinc-300 px-2 py-1.5 text-sm outline-none focus:border-zinc-900"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-zinc-700" data-testid="broadcast-stagger-toggle">
              <input type="checkbox" checked={stagger} onChange={(e) => setStagger(e.target.checked)} className="accent-zinc-900" />
              <span>Space out sends by 5–20s per group <span className="text-zinc-400">(recommended)</span></span>
            </label>

            <button
              data-testid="broadcast-schedule-btn"
              onClick={onSchedule}
              disabled={saving}
              className="w-full bg-zinc-900 py-2.5 text-sm font-medium text-white hover:bg-zinc-700 disabled:opacity-50"
            >
              {saving ? "Scheduling…" : "Schedule broadcast"}
            </button>
          </div>
        </div>

        {/* History */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500">Scheduled &amp; sent</h2>
          {broadcasts.length === 0 && <p className="text-sm text-zinc-400">No broadcasts yet.</p>}
          <div className="space-y-2" data-testid="broadcast-history">
            {broadcasts.map((b) => {
              const results = b.results || [];
              const okCount = results.filter((r) => r.status === "sent").length;
              const failReasons = [
                ...new Set(
                  results
                    .filter((r) => r.status !== "sent" && r.error)
                    .map((r) => `${r.group_name || r.group_id}: ${r.error}`)
                ),
              ];
              return (
                <div key={b.id} className="border border-zinc-200 bg-white p-3" data-testid={`broadcast-item-${b.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      {b.has_image && <ImageIcon size={18} className="mt-0.5 text-zinc-400" />}
                      <div>
                        <p className="text-sm text-zinc-800 line-clamp-2">{b.text || <span className="italic text-zinc-400">(image only)</span>}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
                          <span className="flex items-center gap-1">
                            <ClockIcon size={13} /> {new Date(b.scheduled_at).toLocaleString()}
                          </span>
                          <span>{(b.group_ids || []).length} group(s)</span>
                          {results.length > 0 && (
                            <span className="flex items-center gap-1">
                              <CheckCircleIcon size={13} className="text-green-600" /> {okCount}/{results.length} delivered
                            </span>
                          )}
                        </div>
                        {failReasons.length > 0 && (
                          <div
                            className="mt-2 border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] text-red-700"
                            data-testid={`broadcast-error-${b.id}`}
                          >
                            <span className="font-semibold">Why it failed: </span>
                            {failReasons.join(" · ")}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className={`border px-2 py-0.5 text-[11px] font-medium uppercase ${STATUS_STYLES[b.status] || STATUS_STYLES.scheduled}`}>
                        {b.status}
                      </span>
                      <button
                        data-testid={`broadcast-delete-${b.id}`}
                        onClick={() => onDelete(b.id)}
                        className="text-zinc-400 hover:text-red-600"
                        title="Remove"
                      >
                        <TrashIcon size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Shell>
  );
}
